import React, { useEffect, useRef, useState } from "react";
import { useBranding } from "../context/BrandingContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_URL, getCookie, getHeaders } from "../helper/commonApi";
import Loader from "../components/loader";
import { FaArrowLeftLong } from "react-icons/fa6";

const ResultComponent = () => {
  const { colors } = useBranding();
  const navigate = useNavigate();

  const userId = localStorage.getItem('_register_GRE_LOGIN_userId');

  const [searchParams] = useSearchParams();
  const paramTestId = Number(searchParams.get("tid"));

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("correct");
  const [activeSection, setActiveSection] = useState("Verbal");
  const [date, setDate] = useState(null);

  const [completedTests, setCompletedTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState(null);

  const [essayScore, setEssayScore] = useState(0);

  const [results, setResults] = useState({
    Verbal: { correct: [], incorrect: [], unanswered: [] },
    Quantitative: { correct: [], incorrect: [], unanswered: [] },
    Analytical: { correct: [], incorrect: [], unanswered: [] },
  });

  useEffect(() => {
    const jwtToken = getCookie('_GRE_JWT_TOKEN');

    if (!userId || !jwtToken) {
      localStorage.clear();
      document.cookie = "_GRE_JWT_TOKEN=; max-age=0; path=/;";
      navigate('/')
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // if test_id coming from URL → set it automatically
  useEffect(() => {
    if (paramTestId) {
      setSelectedTestId(paramTestId);
    }
  }, [paramTestId]);

  useEffect(() => {
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getData = async () => {
    const payload = { user_id: userId, subject_id: 1 };
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/dashboard`, payload, {
        headers: getHeaders(),
      });

      if (res.status !== 200) return;

      const data = res.data.data;

      const paid = data.paid_tests || [];
      const completedPaid = paid.filter((t) => t.status === "completed");

      const completedFree =
        data.free_test?.status === "completed" ? [data.free_test] : [];

      const completed = [...completedFree, ...completedPaid];
      setCompletedTests(completed);

      // ===========================================================
      // 1) URL param → highest priority
      // 2) Else completed free test
      // 3) Else first completed paid test
      // ===========================================================

      if (paramTestId) {
        setSelectedTestId(Number(paramTestId));
      } else if (completed.length > 0) {
        setSelectedTestId(completed[0].assigned_test_id);
      } else {
        setSelectedTestId(null);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTestId) {
      fetchResults(Number(selectedTestId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTestId]);


  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  const handleSelect = (id) => {
    setSelectedTestId(Number(id));
    setOpen(false);
  };

  const fetchResults = async (testIdToFetch) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/test-result`,
        {
          assigned_test_id: testIdToFetch,
          user_id: userId,
        },
        { headers: getHeaders() }
      );

      if (res.status === 200) {
        const sections = res.data?.data?.sections || [];

        setDate(res.data?.data?.test_assigned_date);

        // Initialize container
        const sectionWise = {};


        sections.forEach((section) => {
          const sectionType = section.section_type; // Verbal, Quantitative, Analytical
          // const allQuestions = section.questions.map((q) => ({
          //   ...q,
          //   section_type: sectionType,
          // }));

          const allQuestions = section.questions.map((q) => {
            // Extract essay_score if it exists
            if (sectionType === "Analytical") {
              const score = q?.essay_score ?? 0;  // fallback to 0
              setEssayScore(score);
            }

            return {
              ...q,
              section_type: sectionType,
            };
          });

          // If this section type doesn't exist, initialize
          if (!sectionWise[sectionType]) {
            sectionWise[sectionType] = {
              correct: [],
              incorrect: [],
              unanswered: [],
            };
          }

          // Merge into the existing arrays
          sectionWise[sectionType].correct.push(
            ...allQuestions.filter((q) => q?.is_correct === true)
          );

          sectionWise[sectionType].incorrect.push(
            ...allQuestions.filter(
              (q) => q.is_correct === false && q?.user_answer !== "unanswered"
            )
          );

          sectionWise[sectionType].unanswered.push(
            ...allQuestions.filter((q) => q?.user_answer === "unanswered")
          );
        });
        setResults(sectionWise);
      }
    } catch (error) {
      console.error("Error fetching test results:", error);
    } finally {
      setLoading(false);
    }
  };

  const dateObj = new Date(date);

  // Extract values
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth() + 1; // months are 0-based
  const day = dateObj.getDate();

  // Format
  const formatted = `${year}.${month}.${day}`;

  // const questionsToShow = results[activeTab] || [];

  // const total =
  //   (results?.correct?.length || 0) +
  //   (results?.incorrect?.length || 0) +
  //   (results?.unanswered?.length || 0);

  // const getPercent = (count) =>
  //   total > 0 ? ((count / total) * 100).toFixed(0) + "%" : "0%";

  // Tab + Section filtered questions
  const questionsToShow = results[activeSection]?.[activeTab] || [];

  const total =
    (results[activeSection]?.correct?.length || 0) +
    (results[activeSection]?.incorrect?.length || 0) +
    (results[activeSection]?.unanswered?.length || 0);

  const getPercent = (count) =>
    total > 0 ? ((count / total) * 100).toFixed(0) + "%" : "0%";

  return (
    <div>
      {loading ? <Loader /> :
        <div style={{ padding: "0 15px" }}>
          {/* Back to Dashboard button */}
          <div className="container" style={{ marginTop: "20px" }}>
            <button
              onClick={() => navigate("/dashboard")}
              style={{
                padding: "10px 16px",
                background: colors.primaryColor,
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              <span style={{ display: "flex" }}><FaArrowLeftLong /></span><span>Back to Dashboard</span>
            </button>
          </div>
          <div
            className="container bg-white main-border"
            style={{ margin: "20px auto" }}
          >
            <div className="results-card">
              <div className="summary">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                  <div>
                    <h2 className="title" style={{ color: colors?.primaryColor }}>Result Summary</h2>
                    <div className="date">Date: {formatted}</div>
                  </div>

                  {/* <div>
                    <select
                      value={selectedTestId || ""}
                      onChange={(e) => setSelectedTestId(e.target.value)}
                      style={{ padding: "6px" }}
                    >
                      {completedTests.map((test,index) => (
                        <option key={test.assigned_test_id} value={test.assigned_test_id}>
                          {`Test ${index + 1} Result`}
                        </option>
                      ))}
                    </select>
                  </div> */}

                  {/* <div className={`custom-dropdown ${open ? "open" : ""}`} ref={dropdownRef}>
                    <div className="selected" style={{ border: `1px solid ${colors?.primaryColor}` }} onClick={() => setOpen(!open)}>
                      {completedTests.find((t) => t.assigned_test_id === selectedTestId)
                        ? `Test ${completedTests.findIndex(
                          (t) => t.assigned_test_id === selectedTestId
                        ) + 1
                        } Result`
                        : "Select Test"}

                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        className="arrow"
                        style={{
                          transform: open ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>

                    <div className="options">
                      {completedTests.map((test, index) => (
                        <div
                          key={test.assigned_test_id}
                          onClick={() => handleSelect(test.assigned_test_id)}
                        >
                          {`Test ${index + 1} Result`}
                        </div>
                      ))}
                    </div>
                  </div> */}

                  <div
                    className={`custom-dropdown ${open ? "open" : ""}`}
                    ref={dropdownRef}
                  >
                    <div
                      className="selected"
                      style={{
                        border: `1px solid ${colors?.primaryColor}`,
                      }}
                      onClick={() => setOpen(!open)}
                    >
                      {selectedTestId
                        ? `Test ${completedTests.findIndex(
                          (t) => Number(t.assigned_test_id) === Number(selectedTestId)
                        ) + 1
                        } Result`
                        : "Select Test"}

                      <svg
                        width="16"
                        height="16"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        className="arrow"
                        style={{
                          transform: open ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>

                    <div className="options">
                      {completedTests.map((test, index) => (
                        <div
                          key={test.assigned_test_id}
                          onClick={() => handleSelect(test.assigned_test_id)}
                        >
                          {`Test ${index + 1} Result`}
                        </div>
                      ))}
                    </div>
                  </div>

                </div>

                <div className="summary-wrapper">
                  <div className="sidebar">
                    {["Analytical", "Verbal", "Quantitative"].map((section) => (
                      <button
                        key={section}
                        className={`sidebar-tab  ${activeSection === section ? "active" : ""
                          }`}
                        onClick={() => setActiveSection(section)}
                        style={{
                          padding: "12px",
                          margin: "4px 0",
                          width: "110px",
                          background:
                            activeSection === section ? colors.primaryColor : "#f5f5f5",
                          color: activeSection === section ? "#fff" : "#333",
                          fontWeight: activeSection === section ? 600 : 400,
                        }}
                      >
                        {section}
                      </button>
                    ))}
                  </div>

                  {activeSection === "Analytical" && (
                    <div className="essay-score" style={{ marginTop: "12px" }}>
                      Analytical Score: {essayScore}
                    </div>
                  )}

                  <div>
                    <div className="donut-chart">
                      <div className="chart-center">
                        100%
                        <br />
                        Overall
                      </div>

                      {(() => {
                        // const correctPercent = total
                        //   ? ((results[activeSection]?.correct?.length / total) * 100).toFixed(0)
                        //   : 0;
                        // const incorrectPercent = total
                        //   ? ((results[activeSection]?.incorrect?.length / total) * 100).toFixed(0)
                        //   : 0;
                        // const unansweredPercent = total
                        //   ? ((results[activeSection]?.unanswered?.length / total) * 100).toFixed(0)
                        //   : 0;

                        let correctPercent = 0;
                        let incorrectPercent = 0;
                        let unansweredPercent = 0;

                        if (activeSection === "Analytical") {
                          // For analytical section
                          incorrectPercent = essayScore > 0 ? 100 : 0;
                          unansweredPercent = essayScore > 0 ? 0 : 100;
                        } else {
                          // Normal verbal/quant logic
                          correctPercent = total
                            ? (
                              (results[activeSection]?.correct?.length / total) *
                              100
                            ).toFixed(0)
                            : 0;

                          incorrectPercent = total
                            ? (
                              (results[activeSection]?.incorrect?.length / total) *
                              100
                            ).toFixed(0)
                            : 0;

                          unansweredPercent = total
                            ? (
                              (results[activeSection]?.unanswered?.length / total) *
                              100
                            ).toFixed(0)
                            : 0;
                        }

                        return (
                          <svg viewBox="0 0 36 36">
                            <circle className="bg" cx="18" cy="18" r="15.9155" />

                            <circle
                              className="answered"
                              cx="18"
                              cy="18"
                              r="15.9155"
                              strokeDasharray={`${correctPercent}, ${100 - correctPercent}`}
                              strokeDashoffset="0"
                            />

                            <circle
                              className="incorrect"
                              cx="18"
                              cy="18"
                              r="15.9155"
                              strokeDasharray={`${incorrectPercent}, ${100 - incorrectPercent}`}
                              strokeDashoffset={`-${correctPercent}`}
                            />

                            <circle
                              className="unanswered"
                              cx="18"
                              cy="18"
                              r="15.9155"
                              strokeDasharray={`${unansweredPercent}, ${100 - unansweredPercent}`}
                              strokeDashoffset={`-${+correctPercent + +incorrectPercent}`}
                            />
                          </svg>
                        );
                      })()}
                    </div>

                    <div className="legend-item">
                      {activeSection !== "Analytical" && (
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                          <span className="dot green"></span>
                          <span style={{ width: "120px" }}>Correct</span>
                          <div style={{ width: "45px" }}>{getPercent(results[activeSection]?.correct?.length)}</div>
                        </div>
                      )}

                      <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                        <span className="dot red"></span>
                        <span style={{ width: "120px" }}>{activeSection === "Analytical" ? "Answered" : "Incorrect"}</span>
                        {/* <div style={{ width: "45px" }}>{getPercent(results[activeSection]?.incorrect?.length)}</div> */}
                        <div style={{ width: "45px" }}>
                          {activeSection === "Analytical"
                            ? essayScore > 0
                              ? "100%"
                              : "0%"
                            : getPercent(results[activeSection]?.incorrect?.length)}
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                        <span className="dot gray"></span>
                        <span style={{ width: "120px" }}>Unanswered</span>
                        {/* <div style={{ width: "45px" }}>{getPercent(results[activeSection]?.unanswered?.length)}</div> */}
                        <div style={{ width: "45px" }}>
                          {activeSection === "Analytical"
                            ? essayScore > 0
                              ? "0%"
                              : "100%"
                            : getPercent(results[activeSection]?.unanswered?.length)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="upgrade-section" style={{ background: colors.primaryColor, color: colors.textWhite }}>
                  <div className="upgrade-content" >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      <div
                        style={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          backgroundColor: "#000",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                        >
                          <circle cx="12" cy="12" r="12" fill="white" />
                          <path d="M13 3L6 13h5v8l7-10h-5z" fill={colors.primaryColor} />
                        </svg>
                      </div>
                      <h3 style={{ margin: 0 }}>Upgrade Your Plan</h3>
                    </div>
                    <p style={{ color: colors.textWhite, textAlign: "left" }}>
                      Unlock more tests, detailed analytics, and personalized study plans.
                    </p>
                  </div>
                  <button
                    className="upgrade-btn btn dark"
                    style={{ minWidth: "170px" }}
                    onClick={() => { navigate('/pricing') }}
                  >
                    <span> See Pricing Plans </span>
                  </button>
                </div>
              </div>

              <div className="details">
                {activeSection !== "Analytical" && (
                  <>
                    <div className="tabs">
                      <button
                        className={`tab ${activeTab === "correct" ? "active" : ""}`}
                        onClick={() => setActiveTab("correct")}
                      >
                        Correct ({results[activeSection]?.correct?.length})
                      </button>
                      <button
                        className={`tab ${activeTab === "incorrect" ? "active" : ""}`}
                        onClick={() => setActiveTab("incorrect")}
                      >
                        Incorrect ({results[activeSection]?.incorrect?.length})
                      </button>
                      <button
                        className={`tab ${activeTab === "unanswered" ? "active" : ""}`}
                        onClick={() => setActiveTab("unanswered")}
                      >
                        Unanswered ({results[activeSection]?.unanswered?.length})
                      </button>
                    </div>

                    <div className="questions">
                      {questionsToShow.length > 0 ? (
                        questionsToShow.map((q, index) => (
                          <div key={q.user_answer_id} className="question-item">
                            <div className="title">{index + 1}.</div>
                            <div>
                              {/* Handle text or image parts */}
                              {["part1", "part2", "part3", "part4"]
                                .map((key) => q?.[key])
                                .filter(Boolean)
                                .map((part, idx) => (
                                  <span key={idx} style={{ display: "block", marginBottom: "12px" }}>
                                    {part.split("|$|").map((line, lineIdx) =>
                                      line.startsWith("http") ? (
                                        <img
                                          key={lineIdx}
                                          src={line}
                                          alt={`part-${idx}-${lineIdx}`}
                                          style={{ maxWidth: "100%", height: "auto", marginBottom: "6px" }}
                                        />
                                      ) : (
                                        <span key={lineIdx} style={{ display: "block", marginBottom: "6px" }}>
                                          {line}
                                        </span>
                                      )
                                    )}
                                  </span>
                                ))}

                              <p style={{ marginBottom: "5px" }}>
                                <strong>Your Answer:</strong>{" "}
                                {q.user_answer && q.user_answer !== "unanswered" ? q.user_answer : "—"}
                              </p>
                              <p>
                                <strong>Actual Correct Answer:</strong> {q.answer_opt_1 || "N/A"}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p style={{ padding: "10px", color: "#888" }}>
                          No {activeTab} questions available.
                        </p>
                      )}
                    </div>
                  </>
                )}

                {activeSection === "Analytical" && (
                  <>
                    <div className="tabs">
                      <div style={{ padding: "20px 16px", color: "#000000", fontWeight: 600 }}>
                        Answered ({essayScore > 0 ? 1 : 0})
                      </div>
                      <div style={{ padding: "20px 16px", color: "#000000", fontWeight: 600 }}>
                        Unanswered ({essayScore === 0 ? 1 : 0})
                      </div>
                    </div>

                    <div style={{ fontSize: "18px", textAlign: "center", marginTop: "50px", display: "flex", gap: "8px", justifyContent: "center" }}>
                      <span>Score:</span>
                      <span>{essayScore || 0}</span>
                    </div>
                  </>
                )}

              </div>

            </div>
          </div>
        </div >
      }
    </div >
  );
};

export default ResultComponent;
